import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";

/** A rate that named somebody until a team edit took them off the team. */
export type RateLeftUnassigned = {
  rateName: string;
  formerTeamMember: EntityMember;
};

/** A fee schedule as a draft spec or an active redline carries it. */
type FeeScheduleValue = FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | Masked | undefined;

/**
 * The rates a team edit left with no team member.
 *
 * Clearing them is done elsewhere, in the same update as the team change (#5561):
 * `ProposalBuilder.setVendorTeam` on a draft, `withDepartedStaffingUnassigned` on an active proposal.
 * This only reads the difference back out, so the team tab can say which rates it touched: a rate
 * that named a person before the edit and names nobody after it.
 */
export function getRatesLeftUnassigned(
  beforeFeeSchedule: FeeScheduleValue,
  afterFeeSchedule: FeeScheduleValue,
): RateLeftUnassigned[] {
  if (Masked.isMasked(beforeFeeSchedule) || Masked.isMasked(afterFeeSchedule)) return [];

  return (beforeFeeSchedule ?? []).flatMap((rate) => {
    if (!rate.teamMember || rate.teamMemberIsMasked) return [];
    const rateAfter = afterFeeSchedule?.find((candidate) => candidate.id.isEqualTo(rate.id));
    if (!rateAfter || rateAfter.teamMember) return [];
    return [{ rateName: rate.name.value, formerTeamMember: rate.teamMember }];
  });
}

/**
 * What the team tab says when a team edit left rates unassigned: who left, how many rates, and which.
 *
 * @param getPersonName names a former team member; a person it cannot name is still counted.
 */
export function getRatesLeftUnassignedMessage(
  rates: RateLeftUnassigned[],
  getPersonName: (person: EntityMember) => string | undefined,
): string {
  const people = rates
    .map((rate) => rate.formerTeamMember)
    .filter((person, index, all) => all.findIndex((other) => other.isSamePersonAs(person)) === index);
  const names = people.map((person) => getPersonName(person) ?? "A former team member");
  const who = names.length === 1 ? `${names[0]} is` : `${joinWithAnd(names)} are`;
  const count = rates.length === 1 ? "1 hourly rate now has" : `${rates.length} hourly rates now have`;
  const rateNames = rates.map((rate) => rate.rateName).join(", ");
  return `${who} no longer on the team, so ${count} no team member: ${rateNames}.`;
}

function joinWithAnd(items: string[]): string {
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}
